<template>
  <v-menu v-model="showMenu" class="menu" transition="slide-y-transition">
    <template #activator="{ props: menu }">
      <v-tooltip
        location="bottom"
        open-delay="100"
        :text="$t('notification.title')"
      >
        <template #activator="{ props: tooltip }">
          <v-btn
            v-bind="mergeProps(menu, tooltip)"
            icon
            variant="text"
            color="white"
            class="rounded pa-0"
          >
            <v-badge
              :content="notificationCount"
              :model-value="notificationCount !== 0"
              color="primary"
            >
              <v-icon>mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>
      </v-tooltip>
    </template>
    <NotificationList :notifications="notifications" class="list mt-1" />
  </v-menu>
</template>

<script lang="ts">
import { defineComponent, mergeProps } from 'vue';
import { gql } from 'graphql-tag';
import NotificationList from '@/components/app/appBar/Notifications/NotificationList.vue';
import { Notification } from '@/shared/notifications';

export default defineComponent({
  name: 'NotificationBell',
  components: { NotificationList },
  data() {
    return { notifications: [] as Notification[], showMenu: false, mergeProps };
  },
  computed: {
    notificationCount(): number {
      return this.unreadNotifications.length;
    },
    unreadNotifications(): Notification[] {
      return this.notifications.filter(
        (notification) => !notification.hasBeenRead
      );
    }
  },
  watch: {
    showMenu() {
      if (!this.showMenu) {
        void this.readNotifications();
      }
    }
  },
  mounted() {
    void this.$apollo.queries.notifications.refetch();
  },
  methods: {
    readNotifications() {
      const unreadIds = this.unreadNotifications.map(
        (notification) => notification.id
      );

      if (unreadIds.length === 0) {
        return;
      }

      // We clear them early to give the UI a snappy feel.
      this.notifications = this.notifications.map((notification) => ({
        ...notification,
        hasBeenRead: unreadIds.includes(notification.id)
          ? true
          : notification.hasBeenRead
      }));

      unreadIds.forEach((notificationId) => {
        this.$apollo
          .mutate({
            mutation: gql`
              mutation ($input: NotificationToRead!) {
                ReadNotification(input: $input)
              }
            `,
            // Parameters
            variables: {
              input: {
                id: notificationId,
                hasBeenRead: true
              }
            },
            // Update the cache with the result
            // The query will be updated with the optimistic response
            // and then with the real result of the mutation
            // Optimistic UI
            // Will be treated as a 'fake' result as soon as the request is made
            // so that the UI can react quickly and the user be happy
            optimisticResponse: { ReadNotification: 'Success' }
          })
          .then(() => {
            // Result
          })
          .catch(() => {
            // Error
            // We restore the initial user input.
            this.notifications = this.notifications.map((notification) => ({
              ...notification,
              hasBeenRead:
                notification.id === notificationId
                  ? false
                  : notification.hasBeenRead
            }));
          });
      });
    }
  },
  apollo: {
    notifications: {
      query: gql`
        {
          notifications {
            id
            createdAt
            notificationType
            status
            data
            hasBeenRead
          }
        }
      `,
      result({ data }: { data: { notifications: Notification[] } }) {
        this.notifications = data.notifications.map((notification) => ({
          ...notification
        }));
      }
    },
    $subscribe: {
      notification: {
        query: gql`
          subscription {
            receivedNotification {
              id
              notificationType
              status
              data
              createdAt
              hasBeenRead
            }
          }
        `,
        result({
          data
        }: {
          data: {
            receivedNotification: {
              id: string;
              notificationType: string;
              status: string;
              data: object;
              createdAt: string;
              hasBeenRead: boolean;
            };
          };
        }) {
          this.notifications.unshift({ ...data.receivedNotification });
        }
      }
    }
  }
});
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss' as colors;
.v-menu__content {
  box-shadow: 0 0 0 0 !important;
  display: flex;
  align-items: end;
  justify-content: end;
  margin-top: -4px;
}
.menu {
  height: '100%';
}
.v-card {
  border-color: colors.$light-border !important;
  border-width: 1px 2px 2px 1px !important;
  border: solid;
  overflow: scroll;
  overflow-y: auto;
}
.list {
  overflow: scroll;
  overflow-y: auto;
}
</style>
